import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Settings,
  Eye,
  Plus,
  CheckCircle,
  XCircle,
  Edit,
  Loader2,
} from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  fetchAllPlansForAdmin,
  PlanWithFeatures,
} from "@/services/planService";

const PlansPage = () => {
  const [plans, setPlans] = useState<PlanWithFeatures[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await fetchAllPlansForAdmin();

        if (fetchError) {
          setError(fetchError.message);
        } else if (data) {
          setPlans(data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load plans");
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatFeatureLimit = (value: number | null, unit: string = "") => {
    if (value === null || value === 0) return "Unlimited";
    return `${value.toLocaleString()}${unit}`;
  };

  if (loading) {
    return (
      <MainLayout>
        <SuperAdminLayout
          title="Plan Management"
          description="Manage subscription plans and their features"
        >
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </SuperAdminLayout>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <SuperAdminLayout
          title="Plan Management"
          description="Manage subscription plans and their features"
        >
          <Alert variant="destructive">
            <AlertDescription>Error loading plans: {error}</AlertDescription>
          </Alert>
        </SuperAdminLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SuperAdminLayout
        title="Plan Management"
        description="Manage subscription plans and their features"
        actions={
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create Plan
          </Button>
        }
      >
        <div className="space-y-6">
          {/* Plans Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Plans</CardTitle>
              <CardDescription>
                Click on a plan to manage its features and settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan Name</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Key Features</TableHead>
                    <TableHead>Stripe ID</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow
                      key={plan.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() =>
                        (window.location.href = `/super-admin/plans/${plan.id}`)
                      }
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{plan.name}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-xs">
                            {plan.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold">
                          {formatPrice(plan.monthly_price)}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          /mo
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={plan.is_active ? "default" : "secondary"}
                        >
                          {plan.is_active ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          ) : (
                            <XCircle className="h-3 w-3 mr-1" />
                          )}
                          {plan.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline" className="text-xs">
                            {formatFeatureLimit(
                              plan.features.max_active_agents,
                              " agents",
                            )}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {formatFeatureLimit(
                              plan.features.max_conversations_per_month,
                              " convos/mo",
                            )}
                          </Badge>
                          {plan.features.can_web_search && (
                            <Badge variant="outline" className="text-xs">
                              Web Search
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono text-muted-foreground">
                          {plan.stripe_product_id}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div
                          className="flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button asChild size="sm" variant="ghost">
                            <Link to={`/super-admin/plans/${plan.id}`}>
                              <Settings className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default PlansPage;
